import { API_URL } from '../../config'
import store from '../store'
import Vue from 'vue'
import { syncResponse, syncContact, syncRac } from '../util/calidadReporte'

async function qualityRequest(path, method, body) {
  let response
  let data
  try {
    response = await fetch(`${API_URL}/calidad/reporte${path}`, {
      method, headers: { 'Content-Type': 'application/json', Authorization: store.state.token },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    })
    data = await response.json()
  } catch (_) {
    throw new Error('Error de conexión con el reporte de calidad')
  }
  if (!response.ok || !data || data.exito !== 1) throw new Error(data && data.message || 'No se pudo completar la operación')
  return data
}

export default {
  namespaced: true,

  state: {
    filas: [],
    total: 0,
    warnings: [],
    cargando: false
  },

  mutations: {
    set_filas(state, payload) {
      state.filas = Array.isArray(payload) ? payload : []
    },
    set_total(state, payload) {
      state.total = payload
    },
    set_warnings(state, payload) {
      state.warnings = Array.isArray(payload) ? payload : []
    },
    set_cargando(state, payload) {
      state.cargando = payload
    },
    sync_response(state, saved) { state.filas = syncResponse(state.filas, saved) },
    sync_no_encuestable(state, saved) {
      // Keep array identity: presentation-only updates must not clear RAC selection.
      state.filas.forEach(row => {
        if (row.responseKey && row.responseKey === saved.responseKey) Vue.set(row, 'noEncuestable', saved.noEncuestable)
      })
    },
    sync_row_color(state, saved) {
      // Keep array identity: presentation-only updates must not clear RAC selection.
      state.filas.forEach(row => {
        if (row.responseKey && row.responseKey === saved.responseKey) Vue.set(row, 'colorFila', saved.colorFila || null)
      })
    },
    sync_contact(state, saved) { state.filas = syncContact(state.filas, saved) },
    sync_rac(state, results) { state.filas = syncRac(state.filas, results) },
    limpiar_resultados(state) {
      state.filas = []
      state.total = 0
      state.warnings = []
    }
  },

  actions: {
    async fetchPermisos() {
      try {
        const response = await fetch(`${API_URL}/calidad/reporte/permisos`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', Authorization: store.state.token }
        })
        const data = await response.json()
        if (data.exito === 1 && data.permisos) {
          return { gestion: data.permisos.gestion === true, soloConsulta: data.permisos.soloConsulta === true }
        }
        return { gestion: false, soloConsulta: false }
      } catch (error) {
        console.error('[calidadReporte] Error fetching permisos:', error)
        return { gestion: false, soloConsulta: false }
      }
    },

    async getReporte({ commit }, filtros = {}) {
      // `silencioso` es interno del refresco de fondo: no toca el estado de
      // carga ni descarta los datos visibles cuando la recarga falla.
      const { silencioso = false, ...consulta } = filtros
      if (!silencioso) commit('set_cargando', true)
      try {
        let data
        try {
          const response = await fetch(`${API_URL}/calidad/reporte`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: store.state.token
            },
            body: JSON.stringify({
              desde: consulta.desde,
              hasta: consulta.hasta,
              canales: consulta.canales,
              categoriasServicio: consulta.categoriasServicio
            })
          })
          data = await response.json()
        } catch (requestError) {
          throw new Error('Error de conexión al generar el reporte')
        }

        if (data && data.exito === 1) {
          const filas = Array.isArray(data.data) ? data.data : []
          commit('set_filas', filas)
          commit('set_total', data.total !== undefined && data.total !== null ? data.total : filas.length)
          commit('set_warnings', data.warnings)
          return data
        }

        if (!silencioso) commit('limpiar_resultados')
        throw new Error(data && data.message ? data.message : 'Error al generar el reporte')
      } finally {
        if (!silencioso) commit('set_cargando', false)
      }
    },

    async guardarPuntaje({ commit }, payload) {
      const data = await qualityRequest('/puntaje', 'PUT', payload)
      commit('sync_response', data.data)
      return data
    },
    async guardarContacto({ commit }, payload) {
      const data = await qualityRequest('/contacto', 'PUT', payload)
      commit('sync_contact', data.data)
      return data
    },
    async guardarNoEncuestable({ commit }, payload) {
      const data = await qualityRequest('/no-encuestable', 'PUT', payload)
      commit('sync_no_encuestable', data.data)
      return data
    },
    async guardarColorFila({ commit }, payload) {
      const data = await qualityRequest('/color-fila', 'PUT', payload)
      commit('sync_row_color', data.data)
      return data
    },
    getDestinatarios(_context, tipo) { return qualityRequest(`/destinatarios?tipo=${encodeURIComponent(tipo)}`, 'GET') },
    guardarDestinatario(_context, { id, ...body }) {
      return qualityRequest(id ? `/destinatarios/${id}` : '/destinatarios', id ? 'PUT' : 'POST', body)
    },
    bajaDestinatario(_context, { id, tipo }) { return qualityRequest(`/destinatarios/${id}`, 'DELETE', { tipo }) },
    previewRac(_context, payload) { return qualityRequest('/rac/preview', 'POST', payload) },
    previewReenvioRac(_context, payload) { return qualityRequest('/rac/reenviar/preview', 'POST', payload) },
    async enviarRac({ commit }, payload) {
      const result = await qualityRequest('/rac/enviar', 'POST', payload)
      commit('sync_rac', [{ ...payload, estado: 'enviado' }])
      return result
    },
    async reenviarRac({ commit }, payload) {
      const result = await qualityRequest('/rac/reenviar', 'POST', payload)
      commit('sync_rac', [{ ...payload, estado: 'enviado' }])
      return result
    },
    async enviarRacLote({ commit }, payload) {
      const items = [...new Map(payload.items.map(({ canal, sourceId }) =>
        [JSON.stringify([canal, String(sourceId)]), { canal, sourceId }])).values()]
      const results = []
      let error = ''
      for (let offset = 0; offset < items.length; offset += 50) {
        const chunk = items.slice(offset, offset + 50)
        let outcomes
        try {
          const result = await qualityRequest('/rac/lote', 'POST', { items: chunk })
          outcomes = result.data && result.data.results
          const keys = rows => rows.map(item => JSON.stringify([item.canal, String(item.sourceId)])).sort().join('|')
          if (!Array.isArray(outcomes) || outcomes.length !== chunk.length || keys(outcomes) !== keys(chunk) ||
              outcomes.some(item => !['enviado', 'omitido', 'bloqueado', 'incierto', 'error'].includes(item.estado))) {
            throw new Error('Respuesta de lote inválida')
          }
        } catch (failure) {
          error = `${failure.message}. Resultado no confirmado: actualice el reporte y verifique el correo antes de intentar nuevamente.`
          outcomes = chunk.map(item => ({ ...item, estado: 'incierto' }))
        }
        results.push(...outcomes)
        commit('sync_rac', outcomes)
        if (error) break // No retry; remaining chunks were never attempted.
      }
      return { exito: 1, data: { results, total: results.length }, error }
    }
  }
}
